import { AuditResult } from "src/store/auditSlice";
import { Incident, AuditDataItem, IncidentField } from "./types";
import { ALL_FIELD_TYPES } from "./constants";

// Process raw data from API into the format needed for the dashboard
export const processApiData = (
  apiData: Record<string, any>[],
  includeAllFields: boolean = false,
) => {
  const incidents: { [key: string]: Incident } = {};
  const auditItems: AuditDataItem[] = [];

  console.log(`Processing ${apiData.length} raw API records`);

  // First, group by incident_id and find the latest timestamp for each incident
  const incidentGroups: Record<string, Record<string, any>[]> = {};

  apiData.forEach((item) => {
    const incidentId = item.incident_id;
    if (!incidentGroups[incidentId]) {
      incidentGroups[incidentId] = [];
    }
    incidentGroups[incidentId].push(item);
  });

  // For each incident, pick the one with the latest timestamp
  Object.keys(incidentGroups).forEach((incidentId) => {
    const incidentVersions = incidentGroups[incidentId];

    if (incidentVersions.length > 1) {
      console.log(
        `Incident ${incidentId} has ${incidentVersions.length} versions, selecting latest`,
      );
    }

    // Sort by timestamp descending (latest first) and pick the first one
    const latestIncident = incidentVersions.sort((a, b) => {
      const timestampA = new Date(a.timestamp).getTime();
      const timestampB = new Date(b.timestamp).getTime();
      return timestampB - timestampA; // Descending order (latest first)
    })[0];

    // Extract incident data from the latest version
    const item = latestIncident;
    const caseId = item.case_id;
    const timestamp = item.timestamp;
    const dateReported = item.date_reported;
    const reviewer = item.reviewer;
    const auditWorkflowId = item.audit_workflow_id;
    const siteType = item.site_type || "";
    const site = item.site || "";
    const obr_bu = item.obr_bu || "";
    const obr_org = item.obr_org || "";
    const obr_scope = item.obr_scope || "";

    // Create incident with latest data
    incidents[incidentId] = {
      incidentId,
      caseId,
      timestamp,
      dateReported,
      reviewer,
      auditWorkflowId,
      siteType,
      site,
      fields: [],
      dqoCount: 0, // Will be calculated after processing fields
      aiDefectCount: 0, // Will be calculated after processing fields
      aiSource: item.ai_source,
      obr_bu,
      obr_org,
      obr_scope,
    };

    ALL_FIELD_TYPES.forEach((fieldType) => {
      const fieldName = fieldType
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      const austinAnswer = item[`${fieldType}_austin_answer`];
      const llmAnswer = item[`${fieldType}_llm_answer`];
      const ehsAnswer = item[`${fieldType}_ehs_answer`];
      const confirmedAnswer = item[`${fieldType}_confirmed_answer`];
      const auditResult = item[`${fieldType}_audit_result`];

      if (auditResult) {
        const field = {
          fieldName,
          auditResult: auditResult as AuditResult,
          llmAnswer: ehsAnswer || llmAnswer || "", // Use EHS answer if available, otherwise use LLM answer
          austinAnswer: austinAnswer || "",
          confirmedAnswer: confirmedAnswer || "",
        };

        // Add to incident fields
        if (!incidents[incidentId].fields.some((f: IncidentField) => f.fieldName === fieldName)) {
          incidents[incidentId].fields.push(field);
        }

        // Add to flat list
        auditItems.push({
          incidentId,
          caseId: item.case_id,
          timestamp: item.timestamp,
          dateReported: item.date_reported,
          reviewer: item.reviewer,
          fieldName,
          auditResult: auditResult as AuditResult,
          llmAnswer: ehsAnswer || llmAnswer || "",
          austinAnswer: austinAnswer || "",
          confirmedAnswer: confirmedAnswer || "",
        });
      }
    });

    // Calculate DQO count based on Human Defect fields (either core fields only or all fields)
    const dqoFields = incidents[incidentId].fields.filter((field: IncidentField) => {
      // Check if field should be considered based on toggle state
      if (!isFieldRelevant(field.fieldName, includeAllFields)) {
        return false;
      }

      // Handle all field audit results appropriately
      const isHumanDefect =
        field.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
        field.auditResult === AuditResult.NEITHER ||
        field.auditResult === AuditResult.LLM_CORRECT; // For root cause/process path

      return isHumanDefect;
    });

    // Count the number of DQO fields instead of just 0 or 1
    incidents[incidentId].dqoCount = dqoFields.length;

    // Calculate AI Defect count
    const aiDefectFields = incidents[incidentId].fields.filter((field: IncidentField) => {
      // Check if field should be considered based on toggle state
      if (!isFieldRelevant(field.fieldName, includeAllFields)) {
        return false;
      }

      // Handle all field audit results appropriately
      const isAIDefect =
        field.auditResult === AuditResult.HUMAN_CORRECT ||
        field.auditResult === AuditResult.NEITHER;

      return isAIDefect;
    });

    // Count the number of AI defect fields instead of just 0 or 1
    incidents[incidentId].aiDefectCount = aiDefectFields.length;
  });

  console.log(`Processed into ${Object.keys(incidents).length} unique incidents`);

  return {
    incidents: Object.values(incidents),
    auditItems,
  };
};

// Convert audit result to user-friendly defect type
export const getDefectType = (auditResult: AuditResult) => {
  switch (auditResult) {
    case AuditResult.EHS_SMART_ASSISTANT_CORRECT:
    case AuditResult.LLM_CORRECT:
      return "Human Defect";
    case AuditResult.HUMAN_CORRECT:
      return "AI Defect";
    case AuditResult.NEITHER:
      return "Both Defect";
    case AuditResult.BOTH_CORRECT:
    case AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT:
      return "No Defect";
    default:
      return auditResult;
  }
};

// Check if a field is a core field (used for defect counting and filtering)
export const isCoreField = (fieldName: string): boolean => {
  const coreFieldNames = [
    "Incident Location",
    "Primary Impact",
    "Detailed Body Part",
    "Primary Object",
    "Secondary Object",
    "Risk Hazard",
  ];

  return coreFieldNames.includes(fieldName);
};

// Check if a field should be considered for defects and metrics based on user preference
export const isFieldRelevant = (fieldName: string, includeAllFields: boolean): boolean => {
  if (includeAllFields) {
    // Include all fields (core fields + root cause and process path)
    return true;
  } else {
    // Include only core fields
    return isCoreField(fieldName);
  }
};

// Get defect type summary for an incident
export const getDefectTypeSummary = (fieldsWithIssues: IncidentField[]) => {
  const hasHumanDefects = fieldsWithIssues.some(
    (field) =>
      field.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
      field.auditResult === AuditResult.NEITHER ||
      field.auditResult === AuditResult.LLM_CORRECT, // For root cause/process path
  );
  const hasAIDefects = fieldsWithIssues.some(
    (field) =>
      field.auditResult === AuditResult.HUMAN_CORRECT || field.auditResult === AuditResult.NEITHER,
  );

  if (hasHumanDefects && hasAIDefects) return "Human, AI";
  if (hasHumanDefects) return "Human";
  if (hasAIDefects) return "AI";
  return "-";
};
